import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { GpsOption, GpsQuestion, SubForm } from '@via/models';
import { GuidedProcessStepService } from '@via/services';
import { ButtonModule } from 'primeng/button';

import { ODynamicFormComponent } from '../dynamic-form/dynamic-form.component';

/**
 * To use this component:
 *
 *  @example
 *  // 1. Inject following service:
 *  constructor(private guidedProcessStepService: GuidedProcessStepService) {}
 *
 *  // 2. Set the input questions:
 *  this.guidedProcessStepService.InputQuestions = { ... }
 *
 *  // 3. Listen to the observable which emits the answer:
 *  this.guidedProcessStepService.outputAnswers$.subscribe((data) => {
 *    // do whatever you want to do with this data
 *  });
 *
 *  // 4. Start the vGPS component:
 *  this.guidedProcessStepService.toggleVgpsView(true);
 */
@Component({
  selector: 'via-guided-process-step',
  standalone: true,
  imports: [CommonModule, ODynamicFormComponent, ButtonModule],
  templateUrl: './guided-process-step.component.html',
  styleUrls: ['./guided-process-step.component.scss'],
})
export class GuidedProcessStepComponent implements OnInit {
  uniqueInt = 0;
  currIndex = 0;
  questions: GpsQuestion[] = [];
  answers: any[] = [];

  get CurrentQuestion() {
    return this.questions[this.currIndex] || {};
  }

  get Questions() {
    return this.questions.map((question) => question.title);
  }

  get AnsweredQuestions() {
    return this.questions.slice(0, this.answers.length);
  }

  constructor(private guidedProcessStepService: GuidedProcessStepService) {}

  ngOnInit() {
    if (!this.guidedProcessStepService.InputQuestions.length) {
      this.closeGuidedForm();
      return;
    }
    this.questions = this.addIds(
      this.guidedProcessStepService.InputQuestions
    ) as GpsQuestion[];
  }

  getUniqueInt() {
    this.uniqueInt += 1;
    return this.uniqueInt;
  }

  addIds(
    question: GpsQuestion | GpsQuestion[],
    parentId?: number
  ): GpsQuestion | GpsQuestion[] {
    if (Array.isArray(question)) {
      return question.map((q: GpsQuestion) =>
        this.addIds(q, parentId)
      ) as GpsQuestion[];
    }

    const id = this.getUniqueInt();

    if ('options' in question) {
      return {
        id,
        parentId,
        ...question,
        options: question.options.map((option) => ({
          ...option,
          dependents: option.dependents && this.addIds(option.dependents, id),
        })),
      } as GpsQuestion;
    }
    return {
      id,
      parentId,
      ...question,
    } as GpsQuestion;
  }

  getOutputAnswer() {
    return this.answers.reduce((acc, answer, idx) => {
      const question = this.questions[idx];
      return {
        ...acc,
        [question.valueKey]: question.type === 'mcq' ? answer.value : answer,
      };
    }, {});
  }

  closeGuidedForm() {
    this.guidedProcessStepService.toggleVgpsView(false);
  }

  changeQuestion(i: number) {
    if (this.currIndex !== i) {
      this.currIndex = i;
    }
  }

  nextQuestion() {
    if (this.currIndex < this.questions.length - 1) {
      this.currIndex++;
      return;
    }
    this.guidedProcessStepService.OutputAnswers = this.getOutputAnswer();
    this.closeGuidedForm();
  }

  optionSelected(option: GpsOption) {
    if (this.answers?.[this.currIndex]?.value !== option.value) {
      this.answers[this.currIndex] = {
        label: option.label,
        value: option.value,
      };
      this.questions = this.questions.filter(
        (question, i) => i <= this.currIndex || !question.parentId
      );
      this.answers = this.answers.filter((_, i) => i <= this.currIndex);
      if (option.dependents) {
        this.questions.splice(this.currIndex + 1, 0, ...option.dependents);
      }
    } else if (!this.answers?.[this.currIndex]) {
      this.answers.push(option);
    }
    this.nextQuestion();
  }

  subFormSubmitted({ formData: filledData }: { formData: any }) {
    this.answers[this.currIndex] = filledData;
    const currentQuestion = this.questions[this.currIndex] as SubForm;
    this.questions[this.currIndex] = {
      ...currentQuestion,
      fields: currentQuestion.fields.map((field) => ({
        ...field,
        value: filledData[field.key],
      })),
    };
    this.nextQuestion();
  }
}
